import React from 'react';
import { Button, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { darken, alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';

const LoadingButton = ({
  children,
  loading = false,
  success = false,
  error = false,
  disabled = false,
  onClick,
  fullWidth = true,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const brand = theme.palette.primary.main;

  const getBackgroundColor = () => {
    if (success) return '#34D399';
    if (error) return theme.palette.error.main;
    return brand;
  };

  const getHoverColor = () => {
    if (success) return '#059669';
    if (error) return theme.palette.error.dark;
    return darken(brand, 0.15);
  };

  return (
    <>
      <style>
        {`
          @keyframes buttonShake {
            0%, 100% { transform: translateX(0); }
            20%, 60% { transform: translateX(-8px); }
            40%, 80% { transform: translateX(8px); }
          }
          @keyframes spinLoader {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes scaleIn {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>

      <Button
        onClick={onClick}
        disabled={disabled || loading}
        fullWidth={fullWidth}
        variant="contained"
        color="inherit"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          py: '0.9rem',
          borderRadius: '10px',
          fontSize: '0.95rem',
          fontWeight: 700,
          textTransform: 'none',
          backgroundColor: `${getBackgroundColor()} !important`,
          color: '#fff',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          animation: error ? 'buttonShake 0.5s ease' : 'none',
          /* Gradient shine overlay on hover */
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%)',
            opacity: 0,
            transition: 'opacity 0.2s',
          },
          '&:hover': {
            backgroundColor: `${getHoverColor()} !important`,
            transform: disabled || loading ? 'none' : 'translateY(-1px)',
            boxShadow: `0 6px 20px ${alpha(brand, 0.35)}`,
            '&::before': { opacity: 1 },
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            backgroundColor: `${isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.12)'} !important`,
            color: isDark ? 'rgba(203, 213, 225, 0.6)' : 'rgba(71, 85, 105, 0.6)',
            boxShadow: 'none',
          },
        }}
        {...props}
      >
        {/* Button text */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            opacity: loading || success ? 0 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {children}
        </Box>

        {/* Loading spinner */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                border: '2.5px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spinLoader 0.6s linear infinite',
              }}
            />
          </Box>
        )}

        {/* Success checkmark */}
        {success && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              animation: 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            }}
          >
            <CheckIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
        )}
      </Button>
    </>
  );
};

export default LoadingButton;
